import React, { useContext, useRef, useState } from "react";
import styles from "./SearchResultsCarousel.module.scss";
import { FeatureWithDistance } from "utils/sortFeaturesByDistance";
import SearchResultsCarouselItem, {
  SearchResultsCarouselItemProps,
} from "./components/SearchResultsCarouselItem/SearchResultsCarouselItem";
import { parseLanguageObject } from "utils/parseLanguageObject";
import i18n from "i18n";
import { Floors } from "redux/services/config";
import { getFormattedFloors } from "utils/getFormattedFloors";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { FloatingButton, IconButton } from "@livingmap/hippo";
import { useTranslation } from "react-i18next";
import { SearchTag } from "redux/services/config";
import { scaleValue } from "utils";
import { MapContext } from "components/Map/Map";
import classNames from "classnames";

export interface SearchResultsCarouselProps {
  features: FeatureWithDistance[];
  floors: Floors;
  onSelect: (feature: FeatureWithDistance) => void;
  searchTags?: SearchTag[];
  onSearchTagClick?: (searchTag: SearchTag) => void;
  resetTimeouts: () => void;
  isError?: boolean;
}

const SearchResultsCarousel: React.FC<SearchResultsCarouselProps> = ({
  features,
  floors,
  onSelect,
  searchTags = [],
  onSearchTagClick,
  resetTimeouts,
  isError,
}) => {
  const { controlSize } = useContext(MapContext);

  const { t } = useTranslation();
  const formattedFloors = getFormattedFloors(floors);
  const swiperRef = useRef<any>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const mapFeatureToProps = (
    feature: FeatureWithDistance,
    index: number,
  ): SearchResultsCarouselItemProps => {
    const properties = feature.properties || {};
    const floor = formattedFloors.find(
      (formattedFloor) => formattedFloor.id === properties.floor_id,
    );

    return {
      imageUrl: properties.popup_image_url || "",
      ...(properties.popup_header && {
        title: parseLanguageObject(properties.popup_header, i18n.language),
      }),
      ...(properties.popup_subheader && {
        category: parseLanguageObject(
          properties.popup_subheader,
          i18n.language,
        ),
      }),
      ...(floor && { floor: parseLanguageObject(floor.name, i18n.language) }),
      address:
        properties.street_address ||
        t("search_results_carousel.unknown_address"),
      operatingHours: properties.opening_times || {},
      kilometres: feature.distance,
      showNearestLabel: index === 0,
    };
  };

  const showNavigation = features.length > 4.5;

  if (features.length === 0 || isError) {
    return (
      <div className={styles.container}>
        <div className={styles.noResultsWrapper}>
          <div className={styles.noResultsTitle}>
            {isError
              ? t("search_results_carousel.error")
              : t("search_results_carousel.no_results")}
          </div>
          <div className={styles.noResultsMessage}>
            {isError
              ? t("search_results_carousel.error_message")
              : t("search_results_carousel.no_results_message")}
          </div>
          {searchTags.length > 0 && onSearchTagClick && (
            <div className={styles.noResultsTags}>
              {searchTags.map((tag) => (
                <FloatingButton
                  key={tag.id}
                  text={parseLanguageObject(tag.name, i18n.language) || ""}
                  onClick={() => onSearchTagClick(tag)}
                  size="regular"
                  type="primary"
                  // eslint-disable-next-line react/style-prop-object
                  style="filled"
                  dataQA={`search-tag-${tag.id}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {showNavigation && !isBeginning && (
        <div
          className={styles.touchAreaLeft}
          onClick={() => swiperRef.current?.slidePrev()}
          role="button"
          tabIndex={0}
          aria-label="Scroll left"
        >
          <IconButton
            className={styles.navButtonLeft}
            iconSize={24}
            size="large"
            type="primary"
            icon={
              document.documentElement.getAttribute("dir") === "rtl"
                ? "chevron_right"
                : "chevron_left"
            }
            aria-label="Scroll left"
            dataQA="carousel-left"
            backed
          />
        </div>
      )}
      <Swiper
        // loop={showNavigation} Disabled for now to prevent infinite loop
        grabCursor
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        onSlideChange={(swiper) => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd || swiper.activeIndex >= features.length - 4.5);
          resetTimeouts();
        }}
        onScroll={() => {
          resetTimeouts();
        }}
        slidesPerView={"auto"}
        spaceBetween={scaleValue(16, controlSize)}
        className={classNames(styles.swiper, {
          [styles.offset]: !isBeginning && !isEnd,
        })}
      >
        {features.map((feature, index) => (
          <SwiperSlide key={feature.id} className={styles.swiperSlide}>
            <SearchResultsCarouselItem
              {...mapFeatureToProps(feature, index)}
              onClick={() => onSelect(feature)}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      {showNavigation && !isEnd && (
        <div
          className={styles.touchAreaRight}
          onClick={() => swiperRef.current?.slideNext()}
          role="button"
          tabIndex={0}
          aria-label="Scroll right"
        >
          <IconButton
            className={styles.navButtonRight}
            iconSize={24}
            size="large"
            type="primary"
            icon={
              document.documentElement.getAttribute("dir") === "rtl"
                ? "chevron_left"
                : "chevron_right"
            }
            aria-label="Scroll right"
            dataQA="carousel-right"
            backed
          />
        </div>
      )}
    </div>
  );
};

export default SearchResultsCarousel;
